import { NearbyBusinessCategory } from "common/types/nearby-businesses";
import { cn } from "@/lib/utils";
import { useState, useMemo, useRef, useEffect, useCallback, memo } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import { trackEvent } from "common/analytics";
import { getCurrentPageKey } from "@/analytics/pageKey";
import { pinTypeForVenueDetails } from "common/utils";
import { ViewPager, Frame, Track, View } from "@/lib/react-view-pager-compat";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import VenueDetailBasics from "@/components/VenueDetailBasic/BasicVenueDetails";
import VenueAssistantMapDock from "@/components/VenueDetailBasic/VenueAssistantMapDock";
import AccessibilityDetailView from "@/components/VenueDetailBasic/AccessibilityDetailView";
import PhotosDetailView from "@/components/VenueDetailBasic/PhotosDetailView";
import MapViewForNearby from "@/components/WhatsNearby/MapViewForNearby";
import {
  useI18N,
  useSavedPlaces,
  useAuth,
  useNearByFilters,
  useVenueDetailsBySlug,
  useZoomDetection,
} from "common/hooks";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { Storage } from "common/storage";
import VenueCard from "@/components/VenueCard/VenueCard";
import { Venue } from "common/types";
import { useAtom } from "jotai";

import { filtersOpenAtom, leftPanelExpandedAtom, mobileListViewAtom, mapDetailOriginAtom } from "@/state/atoms";
import { ListBulletsIcon, MapTrifoldIcon, HouseIcon, CaretRightIcon, ArrowLeftIcon, ExportIcon, HeartStraightIcon } from "@phosphor-icons/react";
import { Modal } from "react-responsive-modal";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";
import NearbyFiltersSheet from "@/components/WhatsNearby/NearbyFiltersSheet";
import NoSearchResultsFilter from "@/assets/images/noresultsfilter.svg";
import WideButton from "@/components/common/WideButton";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { StandardButton } from "@/components/common/StandardButton";
import { bucketUrlPrefix } from "common/config";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/shadcn/components/ui/accordion";
import Checkbox from "@/components/common/Checkbox";
import { useDarkMode } from "@/hooks/useDarkMode";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import { CaretDownIcon } from "@phosphor-icons/react";
import { RWebShare } from "react-web-share";
import { SlidersHorizontalIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { Tooltip } from "react-tooltip";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";

interface NearbyVenuesProps {
  categories: NearbyBusinessCategory[];
  nearbyBusinessList: any[];
  venueLatitude: number;
  venueLongitude: number;
  venueId: string;
  nearbyFilterAccessibility: any[];
  accessibilityFeaturesRequirementsHiddenIds: string[];
  accessibilityFeaturesRequirementsShowHiddenById: string;
  hasAccessibilityData: boolean;
  venueDetails: Venue;
  businessPhotoFirst: string;
  onClose?: () => void;
  /** Callback when get alert button is clicked */
  onGetAlert?: (venueId: string | number) => void;
  /** Set of venue IDs with notifications enabled */
  notificationVenueIds?: Set<number>;
  /** Authentication state passed from parent */
  isLoggedIn?: boolean;
  /** Handler to show auth modal */
  onShowAuthModal?: () => void;
  /** Current ?venue= query param from TanStack Router (used for back/forward sync) */
  venueParam?: string;
}

function NearbyVenuesNoFilterResults() {
  const i18n = useI18N();
  const [, setIsFilterSheetOpen] = useAtom(filtersOpenAtom);
  const noFilterResultsRef = useRef(null);

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noFilterResultsRef.current) {
      noFilterResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center w-full mt-5 px-4 md:px-0"
      aria-label="No venue found"
    >
      <img
        src={NoSearchResultsFilter}
        alt=""
        className="md:max-w-[50%] max-w-[80%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noFilterResultsRef}
        tabIndex={-1}
        className="mt-3 py-2 md:py-0 text-center title-s-semibold text-fg-neutral-strong"
      >
        {i18n.get("noMatches")}
      </p>
      <p className="label-m-medium py-2 md:py-0 max-w-[444px] mt-2 text-center text-natural-700">
        {i18n.get("tryUpdateFilters")}
      </p>

      <div className="flex justify-center items-center w-full py-6 md:py-3 max-w-[200px]">
        <WideButton
          className="grow"
          onClick={() => {
            setIsFilterSheetOpen(true);
          }}
        >
          {i18n.get("updateFilters")}
        </WideButton>
      </div>
    </div>
  );
}

function NearbyVenuesNoCategoryResults({
  handleCategorySectionTracking,
  setSelectedSection,
}: {
  handleCategorySectionTracking: (section: string) => void;
  setSelectedSection: (section: string) => void;
}) {
  const i18n = useI18N();
  const noFilterResultsRef = useRef(null);

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noFilterResultsRef.current) {
      noFilterResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center w-full mt-5 px-4 md:px-0"
      aria-label="No venue found"
    >
      <img
        src={NoSearchResultsFilter}
        alt=""
        className="md:max-w-[50%] max-w-[80%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noFilterResultsRef}
        tabIndex={-1}
        className="mt-3 py-2 md:py-0 text-center title-s-semibold text-fg-neutral-strong"
      >
        {i18n.get("noNearByVenuesInThisCategory")}
      </p>
      <p className="label-m-medium py-2 md:py-0 max-w-[444px] mt-2 text-center text-natural-700">
        {i18n.get("tryUpdateCategoryFilters")}
      </p>

      <div className="flex justify-center items-center w-full py-6 md:py-3 max-w-[200px]">
        <WideButton
          className="grow"
          onClick={() => {
            handleCategorySectionTracking("All");
            setSelectedSection("All");
          }}
        >
          {i18n.get("viewAllNearbyVenues")}
        </WideButton>
      </div>
    </div>
  );
}

const NearbyVenuesComponent = function NearbyVenues({
  categories,
  nearbyBusinessList,
  venueLatitude,
  venueLongitude,
  venueId,
  nearbyFilterAccessibility,
  accessibilityFeaturesRequirementsHiddenIds,
  accessibilityFeaturesRequirementsShowHiddenById,
  hasAccessibilityData,
  venueDetails,
  businessPhotoFirst,
  onClose,
  onGetAlert,
  notificationVenueIds,
  isLoggedIn = false,
  onShowAuthModal,
  venueParam,
}: NearbyVenuesProps) {
  const [selectedSection, setSelectedSection] = useState<string>("All");
  const [currentVenueTypePage, setCurrentVenueTypePage] = useState(0);
  const [shareUrl, setShareUrl] = useState("");
  useEffect(() => setShareUrl(window.location.href), []);
  const i18n = useI18N();
  const isDark = useDarkMode();
  const venueDetailsScrollRef = useRef<HTMLDivElement>(null);
  const venueListContainerRef = useRef<HTMLDivElement>(null);
  const venueDetailsContainerRef = useRef<HTMLDivElement>(null);
  const { selectedVenue, setSelectedVenue } = useSelectedVenue();
  const trackMobile = useRef<Track>(null);
  const isSyncingFromUrl = useRef(false);
  const [mobileListView, setMobileListView] = useAtom(mobileListViewAtom);
  const [detailOrigin, setDetailOrigin] = useAtom(mapDetailOriginAtom);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const isZoomedIn = useZoomDetection(1.25);
  const { showModal } = useAuthModals();

  const { user } = useAuth();
  const { refetch, data } = useSavedPlaces();
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const savedPlaces = useMemo(
    () => data?.saved_places || [],
    [data?.saved_places],
  );
  const [listNameCreated, setListNameCreated] = useState("");
  const [locationForSavedPlaces, setLocationForSavedPlaces] = useState(0);

  const [isFilterSheetOpen, setIsFilterSheetOpen] = useAtom(filtersOpenAtom);

  // Filter panel state
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterPanelPos, setFilterPanelPos] = useState<{ top: number; right: number } | null>(null);
  const [isMobileScreen, setIsMobileScreen] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobileScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [localAccessibilityOptions, setLocalAccessibilityOptions] = useState<
    number[]
  >([]);
  const [showAllTags, setShowAllTags] = useState<Record<number, boolean>>({});
  const [allSubItemsSelected, setAllSubItemsSelected] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    null,
  );
  const parentIdRef = useRef<number>(
    Number(accessibilityFeaturesRequirementsShowHiddenById),
  );
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const isFirstFilterRender = useRef(true);
  const isAutoApplying = useRef(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const { filtersState, setFiltersState } = useNearByFilters();

  // Search functionality
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isLeftPaneVisible] = useAtom(leftPanelExpandedAtom);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Close the inline filter panel when switching to mobile/zoomed view
  useEffect(() => {
    if ((isZoomedIn || isMobileScreen) && showFilterPanel) {
      setShowFilterPanel(false);
    }
  }, [isZoomedIn, isMobileScreen, showFilterPanel]);

  // Recalculate fixed panel position on window resize while panel is open
  useEffect(() => {
    if (!showFilterPanel) return;
    const recalculate = () => {
      if (filterButtonRef.current) {
        const rect = filterButtonRef.current.getBoundingClientRect();
        setFilterPanelPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
      }
    };
    window.addEventListener("resize", recalculate);
    return () => window.removeEventListener("resize", recalculate);
  }, [showFilterPanel]);

  // State for AccessibilityDetailView
  const [showAccessibilityDetail, setShowAccessibilityDetail] = useState(false);
  const [accessibilityDetailSpaceId, setAccessibilityDetailSpaceId] =
    useState<string>("");

  // State for PhotosDetailView
  const [showPhotosDetail, setShowPhotosDetail] = useState(false);
  const [photosDetailCategoryId, setPhotosDetailCategoryId] = useState<
    number | null
  >(null);

  // Refs for accessibility: focus management on overlays (desktop + mobile pairs)
  const accessibilityDetailRef = useRef<HTMLElement>(null);
  const accessibilityDetailRefMobile = useRef<HTMLElement>(null);
  const photosDetailRef = useRef<HTMLElement>(null);
  const photosDetailRefMobile = useRef<HTMLElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Get user location for venue details API
  const { location } = useGeoLocation();

  // Fetch venue details when showing accessibility or photos detail view
  const { data: venueDetailsData } = useVenueDetailsBySlug({
    slug: selectedVenue?.business?.slug || "",
    latitude: location?.latitude?.toString(),
    longitude: location?.longitude?.toString(),
    accessToken: Storage.getCurrentUserToken() || "",
    enabled:
      (showAccessibilityDetail || showPhotosDetail) &&
      !!selectedVenue?.business?.slug &&
      !!location,
  });

  const [currentSort, setCurrentSort] = useState<
    "closest" | "furthest" | "alphabetical"
  >("closest");

  const [sortByText, setSortByText] = useState("Date added (Newest)");
  useEffect(() => {
    switch (currentSort) {
      case "closest":
        setSortByText(i18n.get("distanceClosest"));
        break;
      case "furthest":
        setSortByText(i18n.get("distanceFurthest"));
        break;
      case "alphabetical":
        setSortByText(i18n.get("alphabetical"));
        break;
      default:
        setSortByText(i18n.get("distanceClosest"));
    }
  }, [currentSort, i18n]);

  // Aria-live announcement state
  const [announceMessage, setAnnounceMessage] = useState("");

  // Track previous values for comparison
  const previousSectionRef = useRef<string>(selectedSection);
  const previousSortRef = useRef<string>(currentSort);
  const previousSearchTermRef = useRef<string>(searchTerm);
  const previousFiltersAppliedRef = useRef<boolean>(filtersState.isFiltersApplied);

  // Sync local accessibility options with filter state (external changes only)
  useEffect(() => {
    if (isAutoApplying.current) {
      isAutoApplying.current = false;
      return;
    }
    setLocalAccessibilityOptions(filtersState.accessibilityOptions);
  }, [filtersState.accessibilityOptions]);

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest("[data-filter-panel]")) {
        setShowFilterPanel(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus management for filter panel
  useEffect(() => {
    if (showFilterPanel && filterPanelRef.current) {
      // Find first focusable element in panel and focus it
      const focusableElements = filterPanelRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    } else if (!showFilterPanel && filterButtonRef.current) {
      // Restore focus to filter button when panel closes
      filterButtonRef.current.focus();
    }
  }, [showFilterPanel]);

  // Keyboard handler for filter panel (Escape key and focus trap)
  useEffect(() => {
    if (!showFilterPanel) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Escape key
      if (event.key === "Escape") {
        event.preventDefault();
        setShowFilterPanel(false);
        return;
      }

      // Handle Tab key for focus trap
      if (event.key === "Tab" && filterPanelRef.current) {
        const focusableElements = filterPanelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey) {
          // Shift+Tab on first element: wrap to last
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab on last element: wrap to first
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showFilterPanel]);

  // Area-map filter funnel (reuses the AccessibilityGuideContent C3 pattern). Resolve selected
  // accessibility tag ids -> tag NAMES for the analytics `selected_filter` list. Plain function
  // (not a hook) so it stays valid below the component's early returns.
  const areaFilterSummary = (ids: number[]) => {
    const nameById = new Map<number, string>();
    (nearbyFilterAccessibility ?? []).forEach((c) =>
      c?.accessibility_tags?.forEach((t: { id: number; name: string }) =>
        nameById.set(t.id, t.name),
      ),
    );
    return {
      filter_type: "accessibility",
      filters_count: ids.length,
      selected_filter: ids.map((id) => nameById.get(id) ?? String(id)),
    };
  };

  // filter_action_button -> filter_opened / filter_closed, driven off the desktop filter panel's
  // open-state so every close path (button, click-outside, Escape, scroll, zoom, clear) fires once.
  // Placed here (before the `if (!data)` early returns below) to keep hook order stable.
  const prevFilterPanelRef = useRef(showFilterPanel);
  useEffect(() => {
    if (showFilterPanel === prevFilterPanelRef.current) return;
    prevFilterPanelRef.current = showFilterPanel;
    trackEvent(
      showFilterPanel ? "filter_opened" : "filter_closed",
      areaFilterSummary(localAccessibilityOptions),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilterPanel]);

  // Single comprehensive effect to manage sub-item selection state
  useEffect(() => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id),
    );

    // CheckIcon if parent is selected
    const hasParent = localAccessibilityOptions.includes(parentId);

    // CheckIcon if all sub-options are selected
    const allSelected =
      hasParent &&
      subOptionIds.every((id) => localAccessibilityOptions.includes(id));

    // Update allSubItemsSelected state only if it changed
    if (allSelected !== allSubItemsSelected) {
      setAllSubItemsSelected(allSelected);
    }
  }, [
    localAccessibilityOptions,
    accessibilityFeaturesRequirementsHiddenIds,
    allSubItemsSelected,
  ]);

  // Apply filters to nearby businesses
  const filteredNearbyBusinesses = useMemo(() => {
    if (
      !filtersState?.accessibilityOptions ||
      filtersState.accessibilityOptions.length === 0
    ) {
      return nearbyBusinessList;
    }

    // Get selected tag IDs
    const selectedTagIds = filtersState.accessibilityOptions;

    // Convert string IDs to numbers for comparison
    const hiddenIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id),
    );
    const parentId = Number(accessibilityFeaturesRequirementsShowHiddenById);

    // Separate tags into step-free options and other options
    const stepFreeOptionIds = selectedTagIds.filter(
      (tagId) => hiddenIds.includes(tagId) || tagId === parentId,
    );

    const otherOptionIds = selectedTagIds.filter(
      (tagId) => !hiddenIds.includes(tagId) && tagId !== parentId,
    );

    // Apply filters to businesses
    return nearbyBusinessList.filter((business) => {
      // Extract business tag IDs from the tagIds string
      // Assuming tag_ids is a string like"1|2|3|4"
      const tagIdsString = business.tag_ids || "";
      const businessTagIds = tagIdsString
        ? tagIdsString.split(",").map((id) => Number(id))
        : [];

      // console.log(`Checking business ${business.id} with tagIds:`, tagIdsString,'parsed:', businessTagIds);

      // CheckIcon step-free options with OR logic
      const hasStepFreeOption =
        stepFreeOptionIds.length === 0 ||
        stepFreeOptionIds.some((filterId) => businessTagIds.includes(filterId));

      // CheckIcon other options with AND logic
      const hasOtherOptions =
        otherOptionIds.length === 0 ||
        otherOptionIds.every((filterId) => businessTagIds.includes(filterId));

      // Business must satisfy both conditions
      const result = hasStepFreeOption && hasOtherOptions;

      // if (result) {
      // console.log(`Business ${business.id} MATCHES filters`);
      // }

      return result;
    });
  }, [
    nearbyBusinessList,
    filtersState.accessibilityOptions,
    accessibilityFeaturesRequirementsHiddenIds,
    accessibilityFeaturesRequirementsShowHiddenById,
  ]);

  useEffect(() => {
    // Skip if we're syncing from URL (to avoid duplicate pushState and analytics)
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
      return;
    }

    // Don't auto-click if we're already on a venue details page
    // (this prevents adding ?venue= query param when loading nearby venues within a complex venue)
    const currentPath = window.location.pathname;
    const isOnVenueDetailsPage = currentPath.startsWith('/venuedetails/') || currentPath.startsWith('/complexvenuedetails/');

    if (selectedVenue && !isOnVenueDetailsPage) {
      onVenueClick(selectedVenue);
    } else if (!selectedVenue) {
      trackMobile.current?.prev();
      setCurrentVenueTypePage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue]);

  // Sync URL venue parameter with selectedVenue state (for browser back/forward buttons)
  useEffect(() => {
    if (venueParam) {
      const matchedVenue = nearbyBusinessList.find(
        (v) => v.business?.slug === venueParam,
      );
      if (matchedVenue && matchedVenue.id !== selectedVenue?.id) {
        isSyncingFromUrl.current = true;
        setSelectedVenue(matchedVenue);
        setCurrentVenueTypePage(1);
        trackMobile.current?.next();
      }
    } else if (!venueParam && selectedVenue) {
      // URL has no venue param but state has one - clear it (back button pressed)
      isSyncingFromUrl.current = true;
      setSelectedVenue(null);
      setCurrentVenueTypePage(0);
      trackMobile.current?.prev();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueParam, nearbyBusinessList]);

  // Accessibility: Manage focus when switching between venue list and details
  useEffect(() => {
    if (selectedVenue && venueDetailsContainerRef.current) {
      venueDetailsContainerRef.current.focus();
    } else if (!selectedVenue && venueListContainerRef.current) {
      venueListContainerRef.current.focus();
    }
  }, [selectedVenue]);

  // Handle Escape key for detail view overlays
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showAccessibilityDetail) {
          setShowAccessibilityDetail(false);
          setAccessibilityDetailSpaceId("");
          // Restore focus to trigger element
          setTimeout(() => {
            triggerElementRef.current?.focus();
            triggerElementRef.current = null;
          }, 0);
        }
        if (showPhotosDetail) {
          setShowPhotosDetail(false);
          setPhotosDetailCategoryId(null);
          // Restore focus to trigger element
          setTimeout(() => {
            triggerElementRef.current?.focus();
            triggerElementRef.current = null;
          }, 0);
        }
      }
    };

    if (showAccessibilityDetail || showPhotosDetail) {
      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }
  }, [showAccessibilityDetail, showPhotosDetail]);

  // Focus management: Move focus to overlay when it opens
  useEffect(() => {
    if (showAccessibilityDetail) {
      // Pick whichever panel is currently visible (offsetParent is null when display:none)
      const visibleEl = [accessibilityDetailRef.current, accessibilityDetailRefMobile.current]
        .find((el) => el && el.offsetParent !== null);
      if (visibleEl) {
        if (!triggerElementRef.current) {
          triggerElementRef.current = document.activeElement as HTMLElement;
        }
        visibleEl.focus();
      }
    }
  }, [showAccessibilityDetail]);

  useEffect(() => {
    if (showPhotosDetail) {
      const visibleEl = [photosDetailRef.current, photosDetailRefMobile.current]
        .find((el) => el && el.offsetParent !== null);
      if (visibleEl) {
        if (!triggerElementRef.current) {
          triggerElementRef.current = document.activeElement as HTMLElement;
        }
        visibleEl.focus();
      }
    }
  }, [showPhotosDetail]);

  const categorySections = useMemo(() => {
    return ["All", ...(categories?.map((category) => category.title) || [])];
  }, [categories]);

  let filteredBusinessesList = useMemo(() => {
    const transformBusiness = (business: any) => ({
      ...business,
      business: {
        ...business.business,
        qrCode: business.business.qr_code,
      },
      photo: business.photo || "",
      photoThumb: business.photo_thumb || "",
      openingHours:
        business.opening_hours?.map((hour: any) => ({
          googleBusinessId: hour.google_business_id,
          day: hour.day,
          openTime: hour.open_time,
          closeTime: hour.close_time,
          id: hour.id,
          utcOffset: hour.utc_offset,
          openTimeUtc: hour.open_time_utc,
          closeTimeUtc: hour.close_time_utc,
          createdAt: hour.created_at,
          updatedAt: hour.updated_at,
          mode: hour.mode,
        })) || [],
      latestReviewDateUpdated: business.latest_review_date_updated || "",
      tagIds: business.tag_ids || "",
      name: business.name || "",
      address: business.address || "",
      userDistance: business.user_distance || "",
      venueSummary: business.business.venue_summary || "",
    });

    if (selectedSection === "All") {
      return filteredNearbyBusinesses.map(transformBusiness);
    }
    return filteredNearbyBusinesses
      .filter((business) =>
        business.nearbyBusinessData?.some(
          (nearby) => nearby.business_nearby_category_title === selectedSection,
        ),
      )
      .map(transformBusiness);
  }, [selectedSection, filteredNearbyBusinesses]);

  // Apply search filter if search term has 3+ characters
  if (searchTerm.length >= 3) {
    const searchLower = searchTerm.toLowerCase();
    filteredBusinessesList = filteredBusinessesList.filter((business) => {
      console.log("business", business);
      const name = business?.name?.toLowerCase() || "";
      const address = business?.address?.toLowerCase() || "";

      return name.includes(searchLower) || address.includes(searchLower);
    });
  }

  // Apply sorting based on currentSort
  const sortedBusinesses = [...filteredBusinessesList].sort((a, b) => {
    switch (currentSort) {
      case "closest": {
        const distanceA = parseFloat(a.userDistance) || 0;
        const distanceB = parseFloat(b.userDistance) || 0;
        return distanceA - distanceB;
      }
      case "furthest": {
        const distanceA = parseFloat(a.userDistance) || 0;
        const distanceB = parseFloat(b.userDistance) || 0;
        return distanceB - distanceA;
      }
      case "alphabetical": {
        const nameA = a.name?.toLowerCase() || "";
        const nameB = b.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      }
      default:
        return 0;
    }
  });

  const filteredBusinesses = sortedBusinesses;

  // Aria-live announcement effects (placed after filteredBusinesses is computed)

  // Announce category changes
  useEffect(() => {
    if (selectedSection !== previousSectionRef.current) {
      previousSectionRef.current = selectedSection;
      const count = filteredBusinesses?.length || 0;
      const message = selectedSection === "All"
        ? `Viewing all categories. ${count} ${i18n.get("results")}.`
        : `Filtered to ${selectedSection}. ${count} ${i18n.get("results")}.`;
      setAnnounceMessage(message);
    }
  }, [selectedSection, filteredBusinesses?.length, i18n]);

  // Announce filter changes
  useEffect(() => {
    if (filtersState.isFiltersApplied !== previousFiltersAppliedRef.current) {
      previousFiltersAppliedRef.current = filtersState.isFiltersApplied;
      if (filtersState.isFiltersApplied) {
        const count = filteredBusinesses?.length || 0;
        setAnnounceMessage(`Filters applied. ${count} ${i18n.get("results")}.`);
      }
    }
  }, [filtersState.isFiltersApplied, filteredBusinesses?.length, i18n]);

  // Announce sort changes
  useEffect(() => {
    if (currentSort !== previousSortRef.current) {
      previousSortRef.current = currentSort;
      const count = filteredBusinesses?.length || 0;
      setAnnounceMessage(`Sorted by ${sortByText}. ${count} ${i18n.get("results")}.`);
    }
  }, [currentSort, sortByText, filteredBusinesses?.length, i18n]);

  // Announce search changes (debounced)
  useEffect(() => {
    if (searchTerm.length >= 3 && searchTerm !== previousSearchTermRef.current) {
      const timeoutId = setTimeout(() => {
        previousSearchTermRef.current = searchTerm;
        const count = filteredBusinesses?.length || 0;
        setAnnounceMessage(`Search results for "${searchTerm}". ${count} ${i18n.get("results")}.`);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else if (searchTerm.length === 0 && previousSearchTermRef.current.length >= 3) {
      previousSearchTermRef.current = searchTerm;
      const count = filteredBusinesses?.length || 0;
      setAnnounceMessage(`Search cleared. ${count} ${i18n.get("results")}.`);
    }
  }, [searchTerm, filteredBusinesses?.length, i18n]);

  const handleCategorySectionTracking = (_section: string) => {};

  // Filter handling functions
  const updateAccessibilityOptions = (newOptions: number[]) => {
    const uniqueOptions = [...new Set(newOptions)];
    setLocalAccessibilityOptions(uniqueOptions);
    // Filters apply live (no Apply button) -> filter_applied on every change.
    trackEvent("filter_applied", areaFilterSummary(uniqueOptions));
  };

  const handleSubOptionClick = (tagId: number, isChecked: boolean) => {
    const currentOptions = [...localAccessibilityOptions];

    if (isChecked) {
      const newOptions = currentOptions.filter((id) => id !== tagId);
      updateAccessibilityOptions(newOptions);
    } else {
      if (!currentOptions.includes(tagId)) {
        const newOptions = [...currentOptions, tagId];
        updateAccessibilityOptions(newOptions);
      }
    }
  };

  const handleAllClick = (isChecked: boolean) => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id),
    );
    const currentOptions = [...localAccessibilityOptions];

    if (isChecked) {
      const newOptions = currentOptions.filter(
        (id) => !subOptionIds.includes(id) || id === parentId,
      );
      updateAccessibilityOptions(newOptions);
    } else {
      const newOptions = currentOptions.includes(parentId)
        ? [...currentOptions]
        : [...currentOptions, parentId];

      const optionsWithSubItems = [...newOptions];
      subOptionIds.forEach((id) => {
        if (!optionsWithSubItems.includes(id)) {
          optionsWithSubItems.push(id);
        }
      });

      updateAccessibilityOptions(optionsWithSubItems);
    }
  };

  const setSelectedOptions = (options: number) => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id),
    );
    const currentOptions = [...localAccessibilityOptions];

    if (!currentOptions.includes(options)) {
      if (options === parentId) {
        const newOptions = [...currentOptions, options, ...subOptionIds];
        updateAccessibilityOptions(newOptions);
        setAllSubItemsSelected(true);
      } else {
        updateAccessibilityOptions([...currentOptions, options]);
      }
    } else {
      if (options === parentId) {
        setAllSubItemsSelected(false);
        const tagsIdsSet = new Set(subOptionIds);
        const newOptions = currentOptions.filter(
          (option) => !tagsIdsSet.has(option) && option !== options,
        );
        updateAccessibilityOptions(newOptions);
      } else {
        const newOptions = currentOptions.filter((o) => o !== options);
        updateAccessibilityOptions(newOptions);
      }
    }
  };

  const isSelected = (id: number) => {
    return localAccessibilityOptions?.includes(id) || false;
  };

  const isAnyOneSelected = (tags: any[]) => {
    const currentIds = tags.map((o) => o.id);
    return localAccessibilityOptions.some((id) => currentIds.includes(id));
  };

  const toggleShowAllTags = (categoryId: number) => {
    setShowAllTags((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleClearFilters = () => {
    // clear_all_action_button -> filter_cleared.
    trackEvent("filter_cleared", {
      filters_count: localAccessibilityOptions.length,
    });
    setLocalAccessibilityOptions([]);
    setAllSubItemsSelected(false);

    setFiltersState((prev) => {
      return {
        ...prev,
        openingHoursSelected: false,
        accessibilityOptions: [],
        isFiltersApplied: false,
        filtersApplied: null,
        selectedBusinessTypes: [],
      };
    });

    setShowFilterPanel(false);
  };

  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    const accessibilities = localAccessibilityOptions
      ?.map((o) => o.toString())
      .join("|");

    isAutoApplying.current = true;

    if (accessibilities.length <= 0) {
      setFiltersState((prev) => ({
        ...prev,
        isFiltersApplied: false,
        filtersApplied: null,
        accessibilityOptions: [],
      }));
    } else {
      setFiltersState((prev) => ({
        ...prev,
        isFiltersApplied: true,
        filtersApplied: { types: "", accessibilities, showVenueOptions: "All" },
        accessibilityOptions: localAccessibilityOptions,
      }));
    }
  }, [localAccessibilityOptions, setFiltersState]);

  const handleSaveVenueAll = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [refetch, user]);

  useEffect(() => {
    if (savedPlaces?.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  }, [savedPlaces?.length, user]);

  const handleSaveVenue = (location) => {
    if (!user) {
      console.log("Please login");
      return;
    }
    console.log("in handleSaveVenue", location);
    setLocationForSavedPlaces(location?.business.id);
    setOpenSavedPlacesModal(true);
  };

  const handleSavedPlacesModalClose = () => {
    console.log(
      "Closing Modal handleSavedPlacesModalClose savePlaceListMode=",
      savePlaceListMode,
    );

    setOpenSavedPlacesModal(false);

    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button and all lists
      handleSaveVenueAll();
    } else {
      setLocationForSavedPlaces(0);
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  const onVenueClick = (location: Venue) => {
    console.log("[onVenueClick] venue clicked:", {
      id: location.id,
      name: location.name,
      slug: location.business?.slug,
      publicUrl: location.business?.publicUrl,
      isComplex: location.business?.isComplex,
      businessType: location.businessType,
    });

    if (location.business.isComplex === 1) {
      const path = location.business.publicUrl || location.business.slug;
      console.log("[onVenueClick] isComplex — opening in new tab:", `/venuedetails/${path}`);
      window.open(`/venuedetails/${path}`, "_blank");
      return;
    } else {
      console.log("[onVenueClick] not complex — opening inline panel for slug:", location.business.slug);
      setCurrentVenueTypePage(1);
      trackMobile.current?.next();
      setSelectedVenue(location);

      // Update browser URL with venue slug as query parameter (avoids route execution)
      const url = new URL(window.location.href);
      url.searchParams.set("venue", location.business.slug);
      window.history.pushState({}, "", url.toString());
      console.log("[onVenueClick] URL updated to:", url.toString());
    }
    
  };

  const isVenueSavedTopLevel = useCallback(() => {
    if (!savedPlaces) {
      return false;
    }
    const flattened = savedPlaces
      ?.map((e) => e.saved_place_details.map((e) => e.business.id))
      .flat();
    const result = flattened.some((id) => id === venueDetails?.business.id);
    return result;
  }, [savedPlaces, venueDetails?.business.id]);

  const handleSaveVenueTopLevel = async () => {
    if (!isLoggedIn || !user) {
      // Show login modal if user is not logged in
      showModal(AuthModalScreen.EnterEmail);
      return;
    }

    // save_place_action_button -> place_save_cta_activated.
    trackEvent("place_save_cta_activated", {
      business_id:
        venueDetails?.business?.id != null
          ? String(venueDetails.business.id)
          : "",
      entry_page: getCurrentPageKey(),
      pin_type: pinTypeForVenueDetails(venueDetails),
      place_name: venueDetails?.name ?? "",
    });

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setLocationForSavedPlaces(venueDetails?.business.id);
    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  if (
    !categories ||
    categories.length === 0 ||
    !nearbyBusinessList ||
    nearbyBusinessList.length === 0
  ) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <p className="text-center label-m-medium text-fg-brand-subtle">
          {i18n.get("noNearbyVenuesFound")}
        </p>
      </div>
    );
  }

  const handleListSort = (value) => {
    console.log("handleListSort value", value);

    // sort_dropdown_sort -> sort_changed. Fires on the area_map (/whats-nearby) surface.
    if (value !== currentSort) {
      trackEvent("sort_changed", {
        previous_sort_method: currentSort,
        sort_method: value,
      });
    }

    setCurrentSort(value as any);
  };

  const handleShare = (_venueDetails: Venue) => {
    console.log("shared successfully!");
    // share_place_action_button -> area_map_share_cta_activated (stub).
    trackEvent("area_map_share_cta_activated", {});
  };

  const handleNotificationClick = useCallback(
    (venue: Venue) => {
      // get_alert_action_button -> place_notification_cta_activated.
      trackEvent("place_notification_cta_activated", {
        business_id: venue?.id != null ? String(venue.id) : "",
        pin_type: pinTypeForVenueDetails(venue),
        place_name: venue?.name ?? "",
      });
      onGetAlert?.(venue.id);
    },
    [onGetAlert],
  );

  return (
    <>

      {/* Desktop Accessibility Detail View - Full screen overlay */}
      {showAccessibilityDetail && venueDetailsData && (
        <section
          ref={accessibilityDetailRef}
          tabIndex={-1}
          aria-label="Accessibility details"
          className="hidden md:flex h-full bg-bg-brand-secondary-default flex-col flex-1"
        >
          {/* AccessibilityDetailView */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <AccessibilityDetailView
              venueDetails={venueDetailsData.details}
              accessibilityCategories={
                venueDetailsData.accesibilityCategories || []
              }
              initialSpaceId={accessibilityDetailSpaceId}
              onClose={() => {
                setShowAccessibilityDetail(false);
                setAccessibilityDetailSpaceId("");
              }}
              onShare={(spaceId) => {
                if (navigator.share) {
                  navigator.share({
                    title: venueDetailsData.details.name,
                    text: `CheckIcon out accessibility information at ${venueDetailsData.details.name}`,
                    url: `${window.location.origin}/accessibility-guide-space/${venueDetailsData.details.business.slug}/${spaceId}`,
                  });
                }
              }}
            />
          </div>
        </section>
      )}

      {/* Photos Detail View - Desktop - Full screen overlay */}
      {venueDetailsData && showPhotosDetail && (
        <section
          ref={photosDetailRef}
          tabIndex={-1}
          aria-label="Venue photos"
          className="hidden md:flex flex-col h-[calc(100vh-90px)] bg-bg-brand-secondary-default"
        >


          {/* PhotosDetailView */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <PhotosDetailView
              venueDetailsResponse={venueDetailsData}
              accessibilityCategories={
                venueDetailsData.accesibilityCategories || []
              }
              initialCategoryId={photosDetailCategoryId}
              venueName={venueDetailsData.details.name}
              onClose={() => {
                setShowPhotosDetail(false);
                setPhotosDetailCategoryId(null);
              }}
            />
          </div>
        </section>
      )}


      {/* Mobile Accessibility Detail View - Full screen overlay */}
      {showAccessibilityDetail && venueDetailsData && (
        <section
          ref={accessibilityDetailRefMobile}
          tabIndex={-1}
          aria-label="Accessibility details"
          className="flex md:hidden h-full bg-bg-brand-secondary-default flex-col flex-1 w-full"
        >
          <div className="flex-1 min-h-0 overflow-hidden">
            <AccessibilityDetailView
              venueDetails={venueDetailsData.details}
              accessibilityCategories={
                venueDetailsData.accesibilityCategories || []
              }
              initialSpaceId={accessibilityDetailSpaceId}
              onClose={() => {
                setShowAccessibilityDetail(false);
                setAccessibilityDetailSpaceId("");
              }}
              onShare={(spaceId) => {
                if (navigator.share) {
                  navigator.share({
                    title: venueDetailsData.details.name,
                    text: `CheckIcon out accessibility information at ${venueDetailsData.details.name}`,
                    url: `${window.location.origin}/accessibility-guide-space/${venueDetailsData.details.business.slug}/${spaceId}`,
                  });
                }
              }}
            />
          </div>
        </section>
      )}

      {/* Photos Detail View - Mobile - Full screen overlay */}
      {venueDetailsData && showPhotosDetail && (
        <section
          ref={photosDetailRefMobile}
          tabIndex={-1}
          aria-label="Venue photos"
          className="flex md:hidden flex-col h-[calc(100vh-90px)] bg-bg-brand-secondary-default"
        >
          <div className="flex-1 min-h-0 overflow-hidden">
            <PhotosDetailView
              venueDetailsResponse={venueDetailsData}
              accessibilityCategories={
                venueDetailsData.accesibilityCategories || []
              }
              initialCategoryId={photosDetailCategoryId}
              venueName={venueDetailsData.details.name}
              onClose={() => {
                setShowPhotosDetail(false);
                setPhotosDetailCategoryId(null);
              }}
            />
          </div>
        </section>
      )}

      <div className={cn(
        "flex flex-col flex-1 min-h-0 bg-bg-brand-secondary-default w-full",
        currentVenueTypePage === 0 && "overflow-y-auto",
        (venueDetailsData && (showAccessibilityDetail || showPhotosDetail)) && "hidden",
      )}>
        <div className={cn(
          "z-40",
          currentVenueTypePage === 0 ? "md:sticky md:top-0" : "sticky top-0"
        )}>
          <div className="bg-bg-brand-secondary-default">
            {/* Header Section */}
            <header className="py-3 bg-surface-secondary border-b border-border-token-brand-minimal flex-shrink-0">
              {/* Breadcrumb Navigation */}
              <div className="px-4 md:px-7 inline-flex justify-start items-start gap-2.5 w-full">
                <nav
                  aria-label="Breadcrumb"
                  className="flex-1 flex justify-start items-center min-w-0"
                >
                  <ol className="max-w-[1440px] mx-auto w-full flex items-center gap-2 list-none m-0 p-0">
                    {/* Venue Name with Home Icon */}
                    <li>
                      {hasAccessibilityData ? (
                        <button
                          type="button"
                          onClick={() => {
                            onClose?.();
                          }}
                          className="min-w-8 min-h-8 bg-white/0 rounded-lg flex items-center gap-1.5 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          aria-label={i18n.get("backToVenueDetails")}
                        >
                          <HouseIcon size={20} className="text-fg-neutral-subtle" weight="fill" />
                          <span className="text-center justify-center text-fg-neutral-strong label-m-medium">{venueDetails?.name}</span>
                        </button>
                      ) : (

                        <button
                          type="button"
                          onClick={() => {
                            window.location.href = "/";
                          }}
                          className="min-w-8 min-h-8 bg-white/0 rounded-lg flex items-center gap-1.5 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          aria-label={i18n.get("backToVenueDetails")}
                        >
                          <ArrowLeftIcon size={20} className="text-fg-neutral-subtle" weight="regular" />
                          <span className="text-center justify-center text-fg-neutral-strong label-m-medium">{i18n.get("home")}</span>
                        </button>

                      )}
                    </li>
                  </ol>
                </nav>
              </div>

              {/* Title Row */}
              <div className="max-w-[1440px] mx-auto px-4 md:px-7 flex flex-col justify-start items-start w-full">
                <div className="inline-flex justify-start items-center gap-3 w-full">
                  <h1 className="flex-1 justify-center text-fg-neutral-strong title-l-bold">
                    {hasAccessibilityData ? i18n.get("whatsNearby") : venueDetails?.name}
                  </h1>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {/* Share Button */}
                    <RWebShare
                      data={{
                        text: i18n.get("shareThisVenue"),
                        url: shareUrl,
                        title: venueDetails.name,
                      }}
                      onClick={() => {
                        handleShare(venueDetails);
                      }}
                    >
                      <button
                        id="nearby-header-share-button"
                        className="flex items-center justify-center w-8 h-8 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        aria-label={i18n.get("shareThisVenue")}
                      >
                        <span className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default rounded-full hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors border border-border-token-brand-minimal">
                          <ExportIcon
                            size={16}
                            weight="regular"
                            aria-hidden="true"
                            className={isDark ? "" : "text-fg-neutral-strong"}
                          />
                        </span>
                      </button>
                    </RWebShare>

                    {/* Save Button */}
                    <button
                      id="nearby-header-save-button"
                      onClick={() => {
                        handleSaveVenueTopLevel();
                      }}
                      className="flex items-center justify-center w-8 h-8 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      aria-label={isVenueSavedTopLevel() ? i18n.get("venueSaved") : i18n.get("saveVenue")}
                    >

                      <span className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default rounded-full hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors border border-border-token-brand-minimal">
                        <HeartStraightIcon
                          size={16}
                          weight={isVenueSavedTopLevel() ? "fill" : "regular"}
                          aria-hidden="true"
                          className={isDark ? "" : "text-fg-neutral-strong"}
                        />
                      </span>

                    </button>

                    <Tooltip
                      anchorSelect="#nearby-header-share-button"
                      content={i18n.get("shareThisVenue")}
                      place="bottom"
                      style={{ zIndex: 9999 }}
                    />
                    <Tooltip
                      anchorSelect="#nearby-header-save-button"
                      content={isVenueSavedTopLevel() ? i18n.get("saved") : i18n.get("save")}
                      place="bottom"
                      style={{ zIndex: 9999 }}
                    />
                  </div>
                </div>
              </div>
            </header>

            {/* Tab Navigation Section */}
            {currentVenueTypePage === 0 && (
            <div>
              <div
                className="max-w-[1440px] mx-auto border-b border-border-token-brand-minimal"
                id="nearby-venues-tabs"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between px-1 py-3 gap-3">
                  {/* Tabs */}
                  <div
                    role="tablist"
                    aria-label="Venue categories"
                    className="flex items-center gap-2 flex-1 relative overflow-hidden"
                  >

                    {/* Right fade gradient */}
                    <div
                      className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white dark:from-[#141415] to-transparent pointer-events-none z-10"
                      aria-hidden="true"
                    />

                    {/* Scrollable section */}
                    <ScrollContainer className="flex flex-nowrap gap-0 overflow-x-auto w-full">
                      {categorySections.map((section, index) => (
                        <button
                          key={section}
                          role="tab"
                          aria-selected={selectedSection === section}
                          aria-controls="venue-list-panel"
                          tabIndex={selectedSection === section ? 0 : -1}
                          className={cn(
                            "whitespace-nowrap flex-shrink-0",
                            selectedSection === section
                              ? "label-m-medium text-fg-brand-subtle px-6 py-3 border-b-2 border-primary-500 focus-blue"
                              : "label-m-medium text-fg-neutral-subtle px-6 py-3 hover:text-fg-neutral-strong focus-blue",
                          )}
                          onClick={() => {
                            handleCategorySectionTracking(section);

                            // If viewing a venue, clear it to navigate back to list
                            // The useEffect will handle the track navigation automatically
                            if (selectedVenue) {
                              setSelectedVenue(null);
                              // Remove venue parameter from URL
                              const url = new URL(window.location.href);
                              url.searchParams.delete("venue");
                              window.history.pushState({}, "", url.toString());
                            }

                            setSelectedSection(section);
                          }}
                          onKeyDown={(e) => {
                            // Arrow key navigation for tabs
                            if (e.key === "ArrowRightIcon") {
                              e.preventDefault();
                              const nextIndex = (index + 1) % categorySections.length;
                              const nextButton = e.currentTarget.parentElement?.children[nextIndex] as HTMLButtonElement;
                              if (nextButton) {
                                nextButton.focus();
                                nextButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                              }
                            } else if (e.key === "ArrowLeftIcon") {
                              e.preventDefault();
                              const prevIndex = index === 0 ? categorySections.length - 1 : index - 1;
                              const prevButton = e.currentTarget.parentElement?.children[prevIndex] as HTMLButtonElement;
                              if (prevButton) {
                                prevButton.focus();
                                prevButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                              }
                            }
                          }}
                          aria-label={section}
                        >
                          {section}
                        </button>
                      ))}
                    </ScrollContainer>
                  </div>

                  {/* Search and Filter â€” desktop only; mobile version is rendered outside this wrapper */}
                  <div
                    className="hidden md:flex items-center gap-3 flex-shrink-0 md:w-auto md:ml-6"
                    id="nearby-venues-search-and-filter"
                  >
                    {/* Search Field */}
                    <div className="relative flex-1 md:flex-initial">
                      <FloatingLabelInput
                        label={i18n.get("search")}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearchTerm(value);

                          // Clear existing timeout
                          if (searchTimeoutRef.current) {
                            clearTimeout(searchTimeoutRef.current);
                          }
                        }}
                        onClear={() => {
                          setSearchTerm("");
                        }}
                      />
                      {searchTerm.length === 0 && (
                        <MagnifyingGlassIcon
                          size={24}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-fg-neutral-subtle"
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    {/* Filter Button */}
                    <div className="relative" data-filter-panel>
                      <button
                        ref={filterButtonRef}
                        onClick={() => {
                          if (isMobileScreen || isZoomedIn) {
                            setIsFilterSheetOpen(true);
                          } else {
                            if (!showFilterPanel && filterButtonRef.current) {
                              const rect = filterButtonRef.current.getBoundingClientRect();
                              setFilterPanelPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
                            }
                            setShowFilterPanel(!showFilterPanel);
                          }
                        }}
                      
                        className={`flex h-12  items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-strong 
                          border rounded-lg transition-colors focus-blue 
                          ${localAccessibilityOptions.length > 0
                          ? "bg-bg-brand-tertiary-default border-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover"
                          : "bg-bg-brand-secondary-default border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover"
                          }`}


                        aria-label={
                          localAccessibilityOptions.length > 0
                            ? `Filter nearby venues, ${localAccessibilityOptions.length} active`
                            : "Filter nearby venues"
                        }
                        aria-haspopup="dialog"
                        aria-expanded={(!isMobileScreen && !isZoomedIn) ? showFilterPanel : undefined}
                        aria-controls={(!isMobileScreen && !isZoomedIn) ? "filter-panel" : undefined}
                      >
                        <SlidersHorizontalIcon
                          size={24}
                          className="text-fg-neutral-subtle"
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                        <span className="text-fg-neutral-strong">{i18n.get("filter")}</span>
                        {localAccessibilityOptions.length > 0 && (
                          <span className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium">
                            {localAccessibilityOptions.length}
                          </span>
                        )}
                      </button>

                      {/* Filter Panel */}
                      {showFilterPanel && !isMobileScreen && !isZoomedIn && (
                        <div
                          id="filter-panel"
                          ref={filterPanelRef}
                          data-filter-panel
                          className="fixed w-[400px] max-h-[600px] bg-bg-brand-secondary-default border border-neutral-100 rounded-xl shadow-lg z-50 flex flex-col"
                          style={{ top: filterPanelPos?.top, right: filterPanelPos?.right }}
                          role="dialog"
                          aria-modal="true"
                          aria-label="Filter options"
                        >
                          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="title-s-semibold text-fg-neutral-strong">
                                {i18n.get("accessibility")} {i18n.get("filters")}
                              </h3>
                              {localAccessibilityOptions.length > 0 && (
                                <StandardButton
                                  variant="link"
                                  className="label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline underline-offset-2"
                                  onClick={handleClearFilters}
                                >
                                  {i18n.get("clearAll")}
                                </StandardButton>
                              )}
                            </div>
                            <div className="space-y-2">
                              {nearbyFilterAccessibility
                                ?.filter(
                                  (category) =>
                                    category.accessibility_tags?.length > 0,
                                )
                                .map((category) => {
                                  const filteredTags =
                                    category.accessibility_tags.filter(
                                      (tag) =>
                                        !accessibilityFeaturesRequirementsHiddenIds.some(
                                          (id) => Number(id) === tag.id,
                                        ),
                                    );

                                  const actualTagsToShow = showAllTags[
                                    category.id
                                  ]
                                    ? filteredTags
                                    : filteredTags.slice(0, 5);

                                  return (
                                    <Accordion
                                      key={category.id}
                                      type="single"
                                      collapsible
                                      value={expandedAccordion}
                                      onValueChange={(value) =>
                                        setExpandedAccordion(value as string)
                                      }
                                    >
                                      <AccordionItem
                                        value={`category-${category.id}`}
                                        className="border-none"
                                      >
                                        <AccordionTrigger
                                          className={`p-2 flex items-center cursor-pointer rounded ${!isAnyOneSelected(filteredTags)
                                            ? "bg-container-brand-minimal"
                                            : "bg-container-brand-minimal"
                                            }`}
                                          aria-label={`${category.name} accessibility filters`}
                                        >
                                          <div className="flex items-center gap-2 flex-grow">
                                            {category.image && (
                                              <img
                                                src={
                                                  bucketUrlPrefix +
                                                  (isDark && category.image_selected
                                                    ? category.image_selected
                                                    : category.image)
                                                }
                                                alt={category.name}
                                                className={`w-6 h-6 flex-shrink-0 ${isDark && !category.image_selected ? "filter brightness-0 invert" : ""}`}
                                                aria-hidden="true"
                                              />
                                            )}
                                            <div className="text-fg-neutral-strong body-l-semibold text-left">
                                              {category.name}
                                            </div>
                                          </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="overflow-visible">
                                          <div className="p-2 flex flex-col gap-2">
                                            {actualTagsToShow.map((tag) => {
                                              if (
                                                accessibilityFeaturesRequirementsHiddenIds.some(
                                                  (id) => Number(id) === tag.id,
                                                )
                                              ) {
                                                return null;
                                              }
                                              return (
                                                <div key={tag.id}>
                                                  <Checkbox
                                                    checked={isSelected(tag.id)}
                                                    onCheck={() =>
                                                      setSelectedOptions(tag.id)
                                                    }
                                                    label={tag.name}
                                                    aria-label={`${tag.name}, ${isSelected(tag.id) ? "selected" : "not selected"}`}
                                                  />
                                                  {tag.id ===
                                                    Number(
                                                      accessibilityFeaturesRequirementsShowHiddenById,
                                                    ) &&
                                                    localAccessibilityOptions.includes(
                                                      tag.id,
                                                    ) && (
                                                      <div key={tag.id + "321"}>
                                                        <p className="mt-2 ml-6 label-m-medium text-fg-neutral-strong">
                                                          {i18n.get(
                                                            "wouldAnyOfTheseAlsoWork",
                                                          )}
                                                        </p>
                                                        <Checkbox
                                                          className="mt-2 ml-6"
                                                          key="all"
                                                          label={i18n.get("all")}
                                                          checked={
                                                            allSubItemsSelected
                                                          }
                                                          onCheck={() =>
                                                            handleAllClick(
                                                              allSubItemsSelected,
                                                            )
                                                          }
                                                          aria-label={`Select all options, ${allSubItemsSelected ? "currently selected" : "currently not selected"}`}
                                                        />
                                                        {accessibilityFeaturesRequirementsHiddenIds.map(
                                                          (tagId) => {
                                                            const matchingTag =
                                                              category.accessibility_tags.find(
                                                                (tag) =>
                                                                  tag.id ===
                                                                  Number(tagId),
                                                              );
                                                            const numTagId =
                                                              Number(tagId);
                                                            const isChecked =
                                                              localAccessibilityOptions.includes(
                                                                numTagId,
                                                              );

                                                            return (
                                                              <Checkbox
                                                                className="mt-2 ml-12"
                                                                key={tagId}
                                                                label={
                                                                  matchingTag
                                                                    ? matchingTag.name
                                                                    : tagId.toString()
                                                                }
                                                                checked={
                                                                  isChecked
                                                                }
                                                                onCheck={() =>
                                                                  handleSubOptionClick(
                                                                    numTagId,
                                                                    isChecked,
                                                                  )
                                                                }
                                                                aria-label={`${matchingTag ? matchingTag.name : tagId.toString()}, ${isChecked ? "selected" : "not selected"}`}
                                                              />
                                                            );
                                                          },
                                                        )}
                                                      </div>
                                                    )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                          {filteredTags.length > 5 && (
                                            <StandardButton
                                              onClick={() => {
                                                toggleShowAllTags(category.id);
                                              }}
                                              variant="blueLink"
                                              className="ml-4 mb-2"
                                              aria-expanded={
                                                !!showAllTags[category.id]
                                              }
                                            >
                                              {!showAllTags[category.id]
                                                ? i18n.get("showMore")
                                                : i18n.get("showLess")}
                                            </StandardButton>
                                          )}
                                        </AccordionContent>
                                      </AccordionItem>
                                    </Accordion>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* â”€â”€ Mobile-only sticky search+filter bar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        {/* Placed as a direct child of the outer scroll container so its containing  */}
        {/* block extends through the venue list â€” required for position:sticky to work */}
        {currentVenueTypePage === 0 && (
          <div className="flex md:hidden items-center gap-3 flex-shrink-0 w-full sticky top-[-2px] z-40 bg-bg-brand-secondary-default border-b border-border-token-brand-minimal px-1 py-3">
            {/* Search Field */}
            <div className="relative flex-1">
              <FloatingLabelInput
                label={i18n.get("search")}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }
                }}
                onClear={() => {
                  setSearchTerm("");
                }}
              />
              {searchTerm.length === 0 && (
                <MagnifyingGlassIcon
                  size={24}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-fg-neutral-subtle"
                  aria-hidden="true"
                />
              )}
            </div>
            {/* Filter Button â€” opens the filter sheet on mobile */}
            <button
              onClick={() => {
                setIsFilterSheetOpen(true);
              }}
              className={`flex items-center gap-2 px-3 py-3 label-m-medium text-fg-neutral-strong border border-border-token-neutral-minimal rounded-lg transition-colors focus-blue flex-shrink-0`}
              aria-label={
                localAccessibilityOptions.length > 0
                  ? `Filter nearby venues, ${localAccessibilityOptions.length} active`
                  : "Filter nearby venues"
              }
              aria-haspopup="dialog"
            >
              <SlidersHorizontalIcon
                size={24}
                className="text-fg-neutral-subtle"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <span className="text-fg-neutral-strong">{i18n.get("filter")}</span>
              {localAccessibilityOptions.length > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium">
                  {localAccessibilityOptions.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* â”€â”€ Desktop layout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div
          className="hidden md:flex flex-1 min-h-0 max-w-[1440px] mx-auto w-full"
        >
          <div
            id="venue-list-panel"
            role="tabpanel"
            aria-label="Venue list"
            className={cn(
              " flex flex-col min-w-0  p-4 md:p-0 md:pl-4  mr-4",
              selectedVenue ? "md:pb-0" : "md:pb-4",
              isLeftPaneVisible ? "flex" : "hidden",
              !isLeftPaneVisible
                ? "w-0"
                : selectedVenue
                  ? "w-[calc(100%-360px)]"
                  : "w-1/2",
            )}
          >

            {/* Venue list â€” hidden when a venue is selected */}
            <div
              ref={venueListContainerRef}
              className={cn(
                "flex-1 overflow-y-auto py-[10px] pb-[20px] pr-[3px]",
                selectedVenue ? "hidden" : "block",
              )}
              aria-hidden={selectedVenue ? "true" : "false"}
              {...(selectedVenue ? { inert: true } : {})}
              tabIndex={-1}
            >
              <div
                ref={venueDetailsScrollRef}
                className="h-full"
              >


                {/* Results Header */}
                {!selectedVenue && (
                  <div className="flex items-center justify-between px-2 pt-2 pb-3">
                    <h2 id="results-heading" className="sr-only">
                      {filteredBusinesses.length} {i18n.get("results")}
                    </h2>

                    <p className="label-m-medium text-fg-neutral-subtle" aria-hidden="true">
                      {filteredBusinesses.length} {i18n.get("results")}
                    </p>
                    {/* Smart screen reader announcements for filter/sort/search/category changes */}
                    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                      {announceMessage}
                    </div>

                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger className="focus-blue rounded">
                          <div className="flex flex-row items-center gap-2 cursor-pointer">
                            <span className="border-b border-primary-800 label-s-medium">
                              {i18n.get("sortColon")} {sortByText}
                            </span>
                            <CaretDownIcon size={20} aria-hidden="true" />
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel aria-label={i18n.get("sortBy")}>
                            {i18n.get("sortColon")}
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuRadioGroup
                            value={currentSort}
                            onValueChange={(value) => {
                              handleListSort(value as any);
                            }}
                          >
                            <DropdownMenuRadioItem value="closest">
                              {i18n.get("distanceClosest")}
                            </DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="furthest">
                              {i18n.get("distanceFurthest")}
                            </DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="alphabetical">
                              {i18n.get("alphabetical")}
                            </DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}




                {filteredBusinesses.length > 0 ? (
                  <ul className="grid pb-10 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-3 ml-2 mr-4 md:gap-4 overflow-visible">
                    {filteredBusinesses.map((business, index) => (
                      <li key={business.id}>
                        <VenueCard
                          location={business}
                          savedPlaces={savedPlaces}
                          onSaveVenue={handleSaveVenue}
                          onOpen={onVenueClick}
                          onNotificationClick={handleNotificationClick}
                          hasNotification={notificationVenueIds?.has(
                            business.business?.id,
                          )}
                          showQRCode={true}
                          truncateText={false}
                          index={index}
                          totalCount={filteredBusinesses.length}
                          showDescription={true}
                          showAddress={false}
                          showOpeningHours={true}
                          showMetaInfo={true}
                          showVariantIndicators={true}
                          enableArrowNavigation={true}
                          showActionButtons={true}
                          isLoggedIn={isLoggedIn}
                          onShowAuthModal={onShowAuthModal}
                        />
                      </li>
                    ))}
                  </ul>
                ) : filtersState.isFiltersApplied ? (
                  <div className="w-full">
                    <NearbyVenuesNoFilterResults />
                  </div>
                ) : (
                  <div className="w-full">
                    <NearbyVenuesNoCategoryResults
                      handleCategorySectionTracking={handleCategorySectionTracking}
                      setSelectedSection={setSelectedSection}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Venue detail â€” shown when a venue is selected */}
            <div
              ref={venueDetailsContainerRef}
              className={cn(
                "flex-1 overflow-y-auto",
                !selectedVenue ? "hidden" : "block",
              )}
              aria-hidden={!selectedVenue ? "true" : "false"}
              {...(!selectedVenue ? { inert: true } : {})}
              tabIndex={-1}
            >
              {selectedVenue && (
                <VenueDetailBasics
                  selectedVenue={selectedVenue}
                  onBack={() => {
                    setSelectedVenue(null);
                    const url = new URL(window.location.href);
                    url.searchParams.delete("venue");
                    window.history.pushState({}, "", url.toString());
                  }}
                  openAllPhotosInline={false}
                  currentVenueTypePage={currentVenueTypePage}
                  onShowDetailView={(initialSpaceId) => {
                    setAccessibilityDetailSpaceId(initialSpaceId);
                    setShowAccessibilityDetail(true);
                  }}
                  onShowPhotosDetailView={(initialCategoryId) => {
                    setPhotosDetailCategoryId(initialCategoryId);
                    setShowPhotosDetail(true);
                  }}
                />
              )}
            </div>
          </div>

          {/* Map pane */}
          <div
            className={cn(
              "min-w-0 mt-4 mr-4 mb-4 relative",
              !isLeftPaneVisible
                ? "w-full"
                : selectedVenue
                  ? "w-[360px]"
                  : "w-1/2",
            )}
          >
            <MapViewForNearby
              filteredBusinesses={filteredBusinesses}
              venueLocation={{
                latitude: venueLatitude,
                longitude: venueLongitude,
              }}
              venueId={venueId}
              hidden={false}
            />
            <VenueAssistantMapDock />
          </div>
        </div>

        {/* â”€â”€ Mobile layout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div
          className={cn(
            "flex md:hidden flex-col w-full",
            currentVenueTypePage !== 0 && "flex-1"
          )}
        >
          {/* Floating map / list toggle button â€” only shown on the list page */}
          {currentVenueTypePage === 0 && (
            <button
              type="button"
              onClick={() => {
                const newView = !mobileListView;
                setMobileListView(newView);
              }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 p-3 rounded-full border-0 cursor-pointer flex items-center gap-2 shadow-lg bg-bg-brand-secondary-default active:bg-bg-brand-secondary-active focus-blue transition-all duration-200"
              aria-label={mobileListView ? i18n.get("showMapView") : i18n.get("showListView")}
            >
              {mobileListView ? (
                <>
                  <MapTrifoldIcon size={24} className="text-fg-neutral-strong" aria-hidden="true" />
                  <span className="label-m-medium text-fg-neutral-strong">{i18n.get("map")}</span>
                </>
              ) : (
                <>
                  <ListBulletsIcon size={24} className="text-fg-neutral-strong" aria-hidden="true" />
                  <span className="label-m-medium text-fg-neutral-strong">{i18n.get("list")}</span>
                </>
              )}
            </button>
          )}

          {/* Mobile content area */}
          <div
            className={cn(
              "pb-[20px]",
              currentVenueTypePage !== 0 && "flex-1 overflow-y-auto",
            )}
          >
            {/* List view â€” shown when mobileListView is true */}
            <div className={cn(mobileListView ? "block" : "hidden")}>
              <ViewPager tag="div" tabIndex={-1} aria-label="Nearby venues content">
                <Frame className="frame">
                  <Track
                    ref={trackMobile}
                    viewsToShow={1}
                    className="track"
                    swipe={false}
                    accessibility={false}
                  >
                    <View>
                      {/* Results Header */}
                      {!selectedVenue && (
                        <div className="flex items-center justify-between px-4 pt-3 pb-1">
                          <h2 id="results-heading-mobile" className="sr-only">
                            {filteredBusinesses.length} {i18n.get("results")}
                          </h2>

                          <p className="label-m-medium text-fg-neutral-subtle" aria-hidden="true">
                            {filteredBusinesses.length} {i18n.get("results")}
                          </p>
                          {/* Smart screen reader announcements for filter/sort/search/category changes */}
                          <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                            {announceMessage}
                          </div>

                          <div className="flex items-center gap-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger className="focus-blue rounded">
                                <div className="flex flex-row items-center gap-2 cursor-pointer">
                                  <span className="border-b border-primary-800 label-s-medium">
                                    {i18n.get("sortColon")} {sortByText}
                                  </span>
                                  <CaretDownIcon size={20} aria-hidden="true" />
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuLabel aria-label={i18n.get("sortBy")}>
                                  {i18n.get("sortColon")}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuRadioGroup
                                  value={currentSort}
                                  onValueChange={(value) => {
                                    handleListSort(value as any);
                                  }}
                                >
                                  <DropdownMenuRadioItem value="closest">
                                    {i18n.get("distanceClosest")}
                                  </DropdownMenuRadioItem>
                                  <DropdownMenuRadioItem value="furthest">
                                    {i18n.get("distanceFurthest")}
                                  </DropdownMenuRadioItem>
                                  <DropdownMenuRadioItem value="alphabetical">
                                    {i18n.get("alphabetical")}
                                  </DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      )}
                      {currentVenueTypePage === 0 && (
                        <div className="px-4">
                          {filteredBusinesses.length > 0 ? (
                            <ul className="grid grid-cols-1 gap-4 py-4 list-none m-0">
                              {filteredBusinesses.map((business, index) => (
                                <li key={business.id}>
                                  <VenueCard
                                    location={business}
                                    savedPlaces={savedPlaces}
                                    onSaveVenue={handleSaveVenue}
                                    onOpen={(loc) => {
                                      // Opened from the mobile list — back should
                                      // return here.
                                      setDetailOrigin("list");
                                      onVenueClick(loc);
                                    }}
                                    onNotificationClick={handleNotificationClick}
                                    hasNotification={notificationVenueIds?.has(
                                      business.business?.id,
                                    )}
                                    showQRCode={true}
                                    truncateText={false}
                                    index={index}
                                    totalCount={filteredBusinesses.length}
                                    showDescription={true}
                                    showAddress={false}
                                    showOpeningHours={true}
                                    showMetaInfo={true}
                                    showVariantIndicators={true}
                                    enableArrowNavigation={true}
                                    showActionButtons={true}
                                    isLoggedIn={isLoggedIn}
                                    onShowAuthModal={onShowAuthModal}
                                  />
                                </li>
                              ))}
                            </ul>
                          ) : filtersState.isFiltersApplied ? (
                            <div className="w-full">
                              <NearbyVenuesNoFilterResults />
                            </div>
                          ) : (
                            <div className="w-full">
                              <NearbyVenuesNoCategoryResults
                                handleCategorySectionTracking={handleCategorySectionTracking}
                                setSelectedSection={setSelectedSection}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </View>
                    <View>
                      {selectedVenue && (
                        <VenueDetailBasics
                          selectedVenue={selectedVenue}
                          onBack={() => {
                            trackMobile.current?.prev();
                            setCurrentVenueTypePage(0);
                            // Return to whichever view the detail was opened
                            // from. List origin keeps the list container visible
                            // so the slide-back animation plays; map origin flips
                            // back to the map the user came from.
                            if (detailOrigin === "map") {
                              setMobileListView(false);
                            }
                            setTimeout(() => {
                              setSelectedVenue(null);
                            }, 500);
                            const url = new URL(window.location.href);
                            url.searchParams.delete("venue");
                            window.history.pushState({}, "", url.toString());
                          }}
                          openAllPhotosInline={false}
                          currentVenueTypePage={currentVenueTypePage}
                          onShowDetailView={(initialSpaceId) => {
                            setAccessibilityDetailSpaceId(initialSpaceId);
                            setShowAccessibilityDetail(true);
                          }}
                          onShowPhotosDetailView={(initialCategoryId) => {
                            setPhotosDetailCategoryId(initialCategoryId);
                            setShowPhotosDetail(true);
                          }}
                        />
                      )}
                    </View>
                  </Track>
                </Frame>
              </ViewPager>
            </div>

            {/* Map view â€” shown when mobileListView is false */}
            <div
              className={cn(mobileListView ? "hidden" : "flex flex-col h-full")}
              role="region"
              aria-label="Map"
            >
              <div className="flex-1 min-h-[600px]">
                <MapViewForNearby
                  filteredBusinesses={filteredBusinesses}
                  venueLocation={{
                    latitude: venueLatitude,
                    longitude: venueLongitude,
                  }}
                  venueId={venueId}
                  hidden={mobileListView}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={venueDetails}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={venueDetails}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}

      <NearbyFiltersSheet
        open={isFilterSheetOpen}
        nearbyFilterAccessibility={nearbyFilterAccessibility}
        accessibilityFeaturesRequirementsHiddenIds={
          accessibilityFeaturesRequirementsHiddenIds
        }
        accessibilityFeaturesRequirementsShowHiddenById={
          accessibilityFeaturesRequirementsShowHiddenById
        }
        onClose={() => {
          setIsFilterSheetOpen(false);
        }}
      />

    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const NearbyVenues = memo(NearbyVenuesComponent);
